import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FiExternalLink, FiChevronDown, FiCalendar, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { getXeroStatus, getXeroReport, getXeroProfitability, getLargestExpenses, getDirectorRemuneration, getFinancialPositionAndCash, getNetProfitMargin, getFinancialRatios } from '@/services/api/xero'
import type { LargestExpenseItem, DirectorRemunerationResponse, FinancialPositionAndCashResponse, NetProfitMarginResponse, FinancialRatiosResponse } from '@/services/api/xero'
import { getDateRange } from '@/utils/getDateRange'
import ProfitabilityCard from '@/components/finance/ProfitabilityCard'
import FinancePageLoading from '@/components/finance/FinancePageLoading'
import XeroStyleTimeSeriesChart from '@/components/finance/XeroStyleTimeSeriesChart'
import { ROUTES } from '@/constants/routes'
import { FINANCE_TABS } from '@/constants/financeTabs'

const XERO_BUSINESS_SNAPSHOT_URL = 'https://go.xero.com/app/!q98Pl/business-snapshot'

type DatePreset = 'last-month' | 'last-quarter' | 'last-financial-year' | 'year-to-date' | 'last-12-months' | 'custom'

function getPresetDates(preset: DatePreset): { from: string; to: string } {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const getLastMonth = () => {
    const y = now.getFullYear()
    const m = now.getMonth()
    const lastMonth = m === 0 ? 11 : m - 1
    const lastMonthYear = m === 0 ? y - 1 : y
    const lastDay = new Date(lastMonthYear, lastMonth + 1, 0).getDate()
    return { from: `${lastMonthYear}-${pad(lastMonth + 1)}-01`, to: `${lastMonthYear}-${pad(lastMonth + 1)}-${pad(lastDay)}` }
  }
  // FY-aligned quarters (Mar–Feb): Q1=Mar–May, Q2=Jun–Aug, Q3=Sep–Nov, Q4=Dec–Feb (matches Xero)
  const getLastQuarter = () => {
    const m = now.getMonth()
    const y = now.getFullYear()
    if (m <= 1) return { from: `${y - 1}-09-01`, to: `${y - 1}-11-30` } // Jan–Feb: last = Q3 Sep–Nov
    if (m <= 4) {
      const isLeap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
      return { from: `${y - 1}-12-01`, to: `${y}-02-${isLeap ? '29' : '28'}` } // Mar–May: last = Q4 Dec–Feb
    }
    if (m <= 7) return { from: `${y}-03-01`, to: `${y}-05-31` } // Jun–Aug: last = Q1 Mar–May
    if (m <= 10) return { from: `${y}-06-01`, to: `${y}-08-31` } // Sep–Nov: last = Q2 Jun–Aug
    return { from: `${y}-09-01`, to: `${y}-11-30` } // Dec: last = Q3 Sep–Nov
  }
  const getLastFinancialYear = () => {
    const y = now.getFullYear()
    const m = now.getMonth()
    const fyEnd = m < 2 ? y - 1 : y
    const fyStart = fyEnd - 1
    const isLeap = (fyEnd % 4 === 0 && fyEnd % 100 !== 0) || fyEnd % 400 === 0
    return { from: `${fyStart}-03-01`, to: `${fyEnd}-02-${isLeap ? '29' : '28'}` }
  }
  const getYearToDate = () => {
    const y = now.getFullYear()
    const m = now.getMonth()
    const startYear = m < 2 ? y - 1 : y
    return { from: `${startYear}-03-01`, to: `${y}-${pad(m + 1)}-${pad(now.getDate())}` }
  }
  const getLast12Months = () => {
    // Last 12 complete calendar months (e.g. Feb 2024 – Jan 2025 when current month is Feb 2025)
    const end = new Date(now.getFullYear(), now.getMonth(), 0)
    const start = new Date(end.getFullYear(), end.getMonth() - 11, 1)
    const lastDay = end.getDate()
    return { from: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`, to: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(lastDay)}` }
  }
  switch (preset) {
    case 'last-month': return getLastMonth()
    case 'last-quarter': return getLastQuarter()
    case 'last-financial-year': return getLastFinancialYear()
    case 'year-to-date': return getYearToDate()
    case 'last-12-months': return getLast12Months()
    default: return { from: '', to: '' }
  }
}

/** Format preset date for dropdown: "Jan 2026" (single month) or "1 Mar 2025 – 17 Feb 2026" (ranges). */
function formatPresetDisplay(preset: DatePreset): string {
  if (preset === 'custom') return ''
  const { from, to } = getPresetDates(preset)
  if (!from || !to) return ''
  const d1 = new Date(from)
  const d2 = new Date(to)
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return ''
  const dayMonthYear = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  const monthYear = (d: Date) => d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
  if (preset === 'last-month') return monthYear(d1)
  return `${dayMonthYear(d1)} – ${dayMonthYear(d2)}`
}

const tabs = FINANCE_TABS

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/** Xero formula: ((current - previous) / |previous|) × 100. Handles previous=0 safely. */
function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return ((current - previous) / Math.abs(previous)) * 100
}

/** Xero-style Y-axis: max absolute value + 15% padding, rounded to clean number. */
function computeYAxisDomain(values: number[]): [number, number] {
  const absValues = values.map((v) => Math.abs(v)).filter((v) => !isNaN(v) && isFinite(v))
  if (absValues.length === 0) return [0, 100]
  const rawMax = Math.max(...absValues, 1)
  const padded = rawMax * 1.15
  const magnitude = Math.pow(10, Math.floor(Math.log10(padded)))
  const roundedMax = Math.ceil(padded / magnitude) * magnitude
  return [0, roundedMax]
}

/** Format date range for chart tooltip (e.g. "1 Jan – 31 Jan 2026"). */
function formatDateRangeForChart(from: string, to: string): string {
  if (!from || !to) return ''
  const d1 = new Date(from)
  const d2 = new Date(to)
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return ''
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  return `${fmt(d1)} – ${fmt(d2)}`
}

/** Same period last year for YoY chart labels. */
function getPreviousYearDates(from: string, to: string): { from: string; to: string } {
  if (!from || !to) return { from: '', to: '' }
  const d1 = new Date(from)
  const d2 = new Date(to)
  d1.setFullYear(d1.getFullYear() - 1)
  d2.setFullYear(d2.getFullYear() - 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    from: `${d1.getFullYear()}-${pad(d1.getMonth() + 1)}-${pad(d1.getDate())}`,
    to: `${d2.getFullYear()}-${pad(d2.getMonth() + 1)}-${pad(d2.getDate())}`,
  }
}

/** Format as "DD Mon YYYY" (e.g. 13 Jan 2026) for tooltip. */
function formatPointDate(date: Date): string {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Parse Xero period string (e.g. "13 Jan 2026" or "Jan 2026") and return same date in previous year for tooltip. */
function periodToPreviousYearLabel(period: string): string {
  if (!period || !period.trim()) return ''
  const d = new Date(period.trim())
  if (isNaN(d.getTime())) return ''
  d.setFullYear(d.getFullYear() - 1)
  return formatPointDate(d)
}

export default function FinanceSnapshot() {
  const location = useLocation()
  const [xeroConnected, setXeroConnected] = useState<boolean | null>(null)
  const [xeroStatus, setXeroStatus] = useState<any>(null)
  const [profitLoss, setProfitLoss] = useState<any>(null)
  const [profitability, setProfitability] = useState<{
    currentProfit: number
    previousProfit: number
    percentage: number
    currentLabel: string
    previousLabel: string
    previousShortLabel: string
    currentMonthAbbr: string
    previousMonthAbbr: string
    currentSalesRevenue: number
    previousSalesRevenue: number
    currentCogs: number
    previousCogs: number
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [largestExpenses, setLargestExpenses] = useState<LargestExpenseItem[] | null>(null)
  const [expenseLabels, setExpenseLabels] = useState<{ currentLabel: string; previousLabel: string }>({ currentLabel: '', previousLabel: '' })
  const [directorRemuneration, setDirectorRemuneration] = useState<DirectorRemunerationResponse | null>(null)
  const [financialPosition, setFinancialPosition] = useState<FinancialPositionAndCashResponse | null>(null)
  const [netProfitMarginResult, setNetProfitMarginResult] = useState<NetProfitMarginResponse | null>(null)
  const [financialRatiosResult, setFinancialRatiosResult] = useState<FinancialRatiosResponse | null>(null)
  const [profitLossPrevious, setProfitLossPrevious] = useState<any>(null)
  const lastMonthDefault = getPresetDates('last-month')
  const [fromDate, setFromDate] = useState(lastMonthDefault.from)
  const [toDate, setToDate] = useState(lastMonthDefault.to)
  const [datePreset, setDatePreset] = useState<DatePreset>('last-month')
  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false)
  const presetDropdownRef = useRef<HTMLDivElement>(null)

  const presetToRangeType = (preset: DatePreset): 'lastMonth' | 'lastQuarter' | 'lastFY' | 'ytd' | 'last12' | null => {
    switch (preset) {
      case 'last-month': return 'lastMonth'
      case 'last-quarter': return 'lastQuarter'
      case 'last-financial-year': return 'lastFY'
      case 'year-to-date': return 'ytd'
      case 'last-12-months': return 'last12'
      default: return null
    }
  }

  useEffect(() => {
    if (datePreset !== 'custom') {
      const rangeType = presetToRangeType(datePreset)
      const range = rangeType ? getDateRange(rangeType) : null
      const nextFrom = range ? range.start : getPresetDates(datePreset).from
      const nextTo = range ? range.end : getPresetDates(datePreset).to
      if (nextFrom && nextTo) {
        setFromDate(nextFrom)
        setToDate(nextTo)
      }
    }
  }, [datePreset])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(e.target as Node)) {
        setPresetDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Function to check and update Xero connection status
  const checkXeroStatus = async () => {
    try {
      const status = await getXeroStatus()
      const isConnected = status?.connected ?? false
      setXeroStatus(status)
      setXeroConnected(isConnected)
      setLoading(isConnected)
    } catch {
      setXeroConnected(false)
      setXeroStatus(null)
      setLoading(false)
    }
  }

  // Check Xero connection status first
  useEffect(() => {
    checkXeroStatus()
  }, [])

  // Load reports when connection status changes to connected
  useEffect(() => {
    if (xeroConnected && !profitLoss && !loading) {
      loadAllReports()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xeroConnected])

  // Refresh status when window regains focus (user might have connected Xero in another tab)
  useEffect(() => {
    const handleFocus = () => {
      if (!xeroConnected) {
        checkXeroStatus()
      }
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [xeroConnected])

  useEffect(() => {
    // Reload reports when date range or connection changes (only if connected)
    if (xeroConnected && fromDate && toDate) {
      loadAllReports()
    }
  }, [xeroConnected, fromDate, toDate])

  const loadAllReports = async () => {
    setLoading(true)
    try {
      const pnlParams: { fromDate?: string; toDate?: string; periods?: string; timeframe?: string; standardLayout?: boolean } = {}

      if (fromDate && toDate) {
        const from = new Date(fromDate)
        const to = new Date(toDate)
        const fromVal = from.getTime()
        const toVal = to.getTime()
        const validRange = fromVal <= toVal
        const pad = (n: number) => String(n).padStart(2, '0')
        if (validRange) pnlParams.fromDate = fromDate
        pnlParams.toDate = `${to.getFullYear()}-${pad(to.getMonth() + 1)}-${pad(to.getDate())}`
        const diffDays = validRange ? Math.ceil((toVal - fromVal) / (1000 * 60 * 60 * 24)) + 1 : 31
        if (diffDays <= 31) {
          pnlParams.timeframe = 'MONTH'
          pnlParams.periods = '11'
        } else if (diffDays <= 365) {
          pnlParams.timeframe = 'MONTH'
          pnlParams.periods = String(Math.min(Math.ceil(diffDays / 30), 11))
        } else {
          pnlParams.timeframe = 'YEAR'
          pnlParams.periods = String(Math.min(Math.ceil(diffDays / 365), 11))
        }
        pnlParams.standardLayout = true
      } else {
        const now = new Date()
        pnlParams.toDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        pnlParams.periods = '11'
        pnlParams.timeframe = 'MONTH'
        pnlParams.standardLayout = true
      }

      const pnl = await getXeroReport('ProfitAndLoss', pnlParams).catch((err) => {
        const errorMsg = err?.response?.data?.error?.message || err?.message || ''
        const isAuthError = err?.response?.status === 401 || errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('authentication failed')
        if (isAuthError) {
          console.warn('ProfitAndLoss: Xero authentication required')
        } else {
          console.warn('ProfitAndLoss error:', errorMsg)
        }
        return { report: null, authError: isAuthError }
      })

      if (fromDate && toDate && pnl?.report) {
        const prev = getPreviousYearDates(fromDate, toDate)
        const pnlParamsPrev = { ...pnlParams, fromDate: prev.from, toDate: prev.to }
        getXeroReport('ProfitAndLoss', pnlParamsPrev)
          .then((res) => setProfitLossPrevious(res?.report ?? null))
          .catch(() => setProfitLossPrevious(null))
      } else {
        setProfitLossPrevious(null)
      }

      const hasAuthError = (pnl as { report?: any; authError?: boolean })?.authError
      if (hasAuthError && !pnl?.report) {
        // Re-check Xero status to see if connection is actually valid
        try {
          const updatedStatus = await getXeroStatus()
          setXeroStatus(updatedStatus)
          setXeroConnected(updatedStatus?.connected ?? false)

          if (!updatedStatus?.connected) {
            toast.error('Xero connection has expired. Please reconnect Xero in Settings → Integrations.')
          } else {
            toast.error('Xero authentication failed. Your connection may need to be refreshed. Please try disconnecting and reconnecting Xero in Settings → Integrations.')
          }
        } catch {
          // If status check fails, assume connection is invalid
          setXeroConnected(false)
          setXeroStatus(null)
          toast.error('Xero connection has expired. Please reconnect Xero in Settings → Integrations.')
        }
      }

      setProfitLoss(pnl?.report)

      if (fromDate && toDate && new Date(fromDate).getTime() <= new Date(toDate).getTime()) {
        getXeroProfitability(fromDate, toDate, xeroStatus?.tenantId)
          .then((data) => setProfitability(data))
          .catch(() => setProfitability(null))
        getLargestExpenses({ fromDate, toDate, tenantId: xeroStatus?.tenantId })
          .then((res) => {
            setLargestExpenses(res.expenses)
            setExpenseLabels({ currentLabel: res.currentLabel, previousLabel: res.previousLabel })
          })
          .catch(() => setLargestExpenses(null))
        getDirectorRemuneration({ fromDate, toDate, tenantId: xeroStatus?.tenantId })
          .then((data) => setDirectorRemuneration(data))
          .catch(() => setDirectorRemuneration(null))
        getFinancialPositionAndCash(toDate, fromDate, xeroStatus?.tenantId)
          .then((data) => setFinancialPosition(data))
          .catch(() => setFinancialPosition(null))
        const filterForBackend = datePreset === 'custom' ? 'custom' : datePreset.replace(/-/g, '_')
        getNetProfitMargin(fromDate, toDate, { filter: filterForBackend, tenantId: xeroStatus?.tenantId })
          .then((data) => setNetProfitMarginResult(data))
          .catch(() => setNetProfitMarginResult(null));
        const rangeStart = fromDate.trim()
        const rangeEnd = toDate.trim()
        const startMs = new Date(rangeStart).getTime()
        const endMs = new Date(rangeEnd).getTime()
        const [financialFrom, financialTo] = startMs <= endMs ? [rangeStart, rangeEnd] : [rangeEnd, rangeStart]
        getFinancialRatios(financialFrom, financialTo, xeroStatus?.tenantId)
          .then((data) => setFinancialRatiosResult(data))
          .catch(() => setFinancialRatiosResult(null))
      } else {
        setProfitability(null)
        setLargestExpenses(null)
        setDirectorRemuneration(null)
        setFinancialPosition(null)
        setNetProfitMarginResult(null)
        setFinancialRatiosResult(null)
        setProfitLossPrevious(null)
      }
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error?.message || e?.message || 'Failed to load Business Snapshot'
      console.error('Error loading reports:', e)

      // Check if it's an auth error
      const isAuthError = e?.response?.status === 401 || errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('authentication failed')
      if (isAuthError) {
        // Re-check status
        try {
          const updatedStatus = await getXeroStatus()
          setXeroStatus(updatedStatus)
          setXeroConnected(updatedStatus?.connected ?? false)
        } catch {
          setXeroConnected(false)
          setXeroStatus(null)
        }
        toast.error('Xero authentication failed. Please reconnect Xero in Settings → Integrations.')
      } else {
        toast.error(errorMsg)
      }
    } finally {
      setLoading(false)
    }
  }

  const flattenRows = (rows: any[]): any[] => {
    const out: any[] = []
    for (const r of rows || []) {
      if (r.RowType === 'Row' || r.RowType === 'SummaryRow') {
        out.push(r)
      } else if (r.RowType === 'Section' && r.Rows?.length) {
        out.push(...flattenRows(r.Rows))
      }
    }
    return out
  }

  const getPnlPeriodsToSum = (): number => {
    if (!fromDate || !toDate) return 1
    const diffDays = Math.ceil((new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000) + 1
    return diffDays > 31 ? 999 : 1
  }

  const extractExpensesFromPnl = (report: any): { costOfSales: number; operatingExpenses: number; totalExpenses: number } => {
    let costOfSales = 0
    let operatingExpenses = 0
    if (!report?.Rows) return { costOfSales: 0, operatingExpenses: 0, totalExpenses: 0 }
    const getSummaryValue = (cells: any[]): number => {
      const val = cells?.[1]?.Value ?? cells?.[1]?.value
      return Math.abs(parseNumericValue(val))
    }
    const findSummaryRow = (rows: any[]): any => {
      const direct = (rows || []).find((r: any) => (r?.RowType ?? r?.rowType) === 'SummaryRow')
      if (direct) return direct
      for (const r of rows || []) {
        const nested = r?.Rows ?? r?.rows
        if (nested?.length) {
          const found = findSummaryRow(nested)
          if (found) return found
        }
      }
      return null
    }
    const walk = (rows: any[]) => {
      for (const row of rows || []) {
        const rowType = row?.RowType ?? row?.rowType
        const title = (row?.Title ?? row?.title ?? '').toString().toLowerCase()
        const nestedRows = row?.Rows ?? row?.rows ?? []
        if (rowType === 'Section') {
          const summary = findSummaryRow(nestedRows)
          const cells = summary?.Cells ?? summary?.cells ?? []
          const val = getSummaryValue(cells)
          if (title.includes('less cost of sales') || title.includes('cost of sales') || title.includes('cost of goods') || title.includes('cogs') || title.includes('direct costs')) {
            costOfSales = val
          } else if (title.includes('operating expenses') || title.includes('operating expenditure')) {
            operatingExpenses = val
          }
          walk(nestedRows)
        }
      }
    }
    walk(report.Rows)
    return { costOfSales, operatingExpenses, totalExpenses: costOfSales + operatingExpenses }
  }

  const extractValue = (report: any, labelPattern: string | string[], periodsToSum?: number): number => {
    if (!report || !report.Rows) return 0
    const patterns = Array.isArray(labelPattern) ? labelPattern : [labelPattern]
    const sumCols = periodsToSum ?? 1
    const collectValues = (cells: any[]): number => {
      let total = 0
      for (let i = 1; i < cells.length && i <= sumCols; i++) {
        const numValue = parseNumericValue(cells[i]?.Value)
        if (!isNaN(numValue)) total += numValue
      }
      return total === 0 ? 0 : Math.abs(total)
    }
    const summaryRows = flattenRows(report.Rows).filter((r: any) => r.RowType === 'SummaryRow')
    for (const row of summaryRows) {
      if (row.Cells && row.Cells.length > 0) {
        const label = (row.Cells[0]?.Value || '').toLowerCase().trim()
        for (const pattern of patterns) {
          if (label === pattern.toLowerCase() || label.includes(pattern.toLowerCase())) {
            const v = collectValues(row.Cells)
            if (v !== 0) return v
          }
        }
      }
    }
    return 0
  }

  const parseNumericValue = (value: any): number => {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      // Remove currency symbols, commas, and whitespace
      const cleanValue = value.replace(/[^-\d.]/g, '')
      return parseFloat(cleanValue) || 0
    }
    return 0
  }

  const findHeaderRow = (rows: any[]): any => {
    if (!rows?.length) return null
    const header = rows.find((r: any) => r.RowType === 'Header')
    if (header) return header
    for (const r of rows) {
      if (r.RowType === 'Section' && r.Rows?.length) {
        const nested = findHeaderRow(r.Rows)
        if (nested) return nested
      }
    }
    return null
  }

  const extractTimeSeriesData = (report: any, labelPattern: string | string[]): { period: string; current: number; previous: number }[] => {
    if (!report || !report.Rows) return []
    const headerRow = findHeaderRow(report.Rows)
    if (!headerRow?.Cells || headerRow.Cells.length < 2) return []
    const periods = headerRow.Cells.slice(1).map((c: any) => String(c.Value || '').trim() || '').filter(Boolean)
    if (periods.length === 0) return []
    const patterns = Array.isArray(labelPattern) ? labelPattern : [labelPattern]
    const data: { period: string; current: number; previous: number }[] = periods.map((period: string, idx: number) => ({
      period: period || `Period ${idx + 1}`,
      current: 0,
      previous: 0,
    }))
    const extractFromRow = (row: any) => {
      if (!row.Cells || row.Cells.length < 2 || !row.Cells[0]) return false
      const label = (row.Cells[0].Value || '').toLowerCase().trim()
      for (const pattern of patterns) {
        if (label.includes(pattern.toLowerCase())) {
          periods.forEach((_: string, idx: number) => {
            if (row.Cells[idx + 1]) {
              const val = parseNumericValue(row.Cells[idx + 1].Value)
              if (!isNaN(val)) data[idx].current = Math.abs(val)
            }
          })
          return true
        }
      }
      return false
    }
    const summaryRows = flattenRows(report.Rows).filter((r: any) => r.RowType === 'SummaryRow')
    let found = summaryRows.some(extractFromRow)
    if (!found) {
      const allRows = flattenRows(report.Rows)
      found = allRows.some(extractFromRow)
    }
    if (!found) return []
    data.reverse()
    for (let i = 1; i < data.length; i++) {
      data[i].previous = data[i - 1].current
    }
    return data
  }

  /** Same as extractTimeSeriesData but uses previousReport for true same-month-last-year (YoY). */
  const extractTimeSeriesDataWithComparison = (
    currentReport: any,
    previousReport: any,
    labelPattern: string | string[]
  ): { period: string; current: number; previous: number }[] => {
    if (!currentReport?.Rows) return []
    const headerRow = findHeaderRow(currentReport.Rows)
    if (!headerRow?.Cells || headerRow.Cells.length < 2) return []
    const periods = headerRow.Cells.slice(1).map((c: any) => String(c.Value || '').trim() || '').filter(Boolean)
    if (periods.length === 0) return []
    const patterns = Array.isArray(labelPattern) ? labelPattern : [labelPattern]
    const getValuesForReport = (report: any): number[] => {
      const values = new Array(periods.length).fill(0)
      const extractFromRow = (row: any) => {
        if (!row.Cells || row.Cells.length < 2 || !row.Cells[0]) return false
        const label = (row.Cells[0].Value || '').toLowerCase().trim()
        for (const pattern of patterns) {
          if (label.includes(pattern.toLowerCase())) {
            periods.forEach((_: string, idx: number) => {
              if (row.Cells[idx + 1]) {
                const val = parseNumericValue(row.Cells[idx + 1].Value)
                if (!isNaN(val)) values[idx] = Math.abs(val)
              }
            })
            return true
          }
        }
        return false
      }
      const summaryRows = flattenRows(report.Rows).filter((r: any) => r.RowType === 'SummaryRow')
      summaryRows.some(extractFromRow) || flattenRows(report.Rows).some(extractFromRow)
      return values
    }
    const currentValues = getValuesForReport(currentReport)
    const prevValues = previousReport?.Rows ? getValuesForReport(previousReport) : new Array(periods.length).fill(0)
    const data = periods.map((period: string, idx: number) => ({
      period: period || `Period ${idx + 1}`,
      current: currentValues[idx] ?? 0,
      previous: prevValues[idx] ?? 0,
    }))
    data.reverse()
    return data
  }

  const pnlPeriodsToSum = getPnlPeriodsToSum()
  const profitLossValue = extractValue(profitLoss, ['profit or loss', 'net profit/loss', 'net profit', 'net income', 'total profit', 'profit/(loss)'], pnlPeriodsToSum)
  const incomeValue = extractValue(profitLoss, ['total income', 'income', 'revenue', 'sales', 'total revenue', 'total sales'], pnlPeriodsToSum)
  const { costOfSales: costOfGoodsSold, operatingExpenses, totalExpenses: expensesValue } = extractExpensesFromPnl(profitLoss)

  const profitData = profitLossPrevious
    ? extractTimeSeriesDataWithComparison(profitLoss, profitLossPrevious, ['profit or loss', 'net profit/loss', 'net profit', 'net income'])
    : extractTimeSeriesData(profitLoss, ['profit or loss', 'net profit/loss', 'net profit', 'net income'])
  const incomeData = profitLossPrevious
    ? extractTimeSeriesDataWithComparison(profitLoss, profitLossPrevious, ['income', 'revenue', 'sales'])
    : extractTimeSeriesData(profitLoss, ['income', 'revenue', 'sales'])
  const expensesData = profitLossPrevious
    ? extractTimeSeriesDataWithComparison(profitLoss, profitLossPrevious, ['total expenses', 'total operating expenses', 'expenses'])
    : extractTimeSeriesData(profitLoss, ['total expenses', 'total operating expenses', 'expenses'])

  // Filter time-series rows so chart only shows points inside the selected date range
  const filterSeriesByRange = (
    data: { period: string; current: number; previous: number }[]
  ): { period: string; current: number; previous: number }[] => {
    if (!fromDate || !toDate) return data
    const start = new Date(fromDate)
    const end = new Date(toDate)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return data
    return data.filter((d) => {
      const parsed = new Date(d.period.trim())
      if (isNaN(parsed.getTime())) return true
      const t = parsed.getTime()
      return t >= start.getTime() && t <= end.getTime()
    })
  }

  const incomeSeries = filterSeriesByRange(incomeData)
  const expensesSeries = filterSeriesByRange(expensesData)

  const chartDiffDays = fromDate && toDate ? Math.ceil((new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000) + 1 : 31
  const chartGroupBy: 'DAY' | 'MONTH' = chartDiffDays <= 31 ? 'DAY' : 'MONTH'
  const formatChartPeriod = (v: string, groupBy: 'DAY' | 'MONTH'): string => {
    if (!v) return ''
    if (groupBy === 'DAY') { const m = v.match(/(\d{1,2})\s+\w+\s+\d{2,4}/); return m ? m[1] : v.slice(0, 5) }
    const m = v.match(/(\w+)\s+(\d{2,4})/)
    return m ? `${m[1].slice(0, 3)} ${m[2]?.slice(-2) || ''}` : (v.length > 10 ? v.slice(0, 7) : v)
  }
  const incomeYDomain = computeYAxisDomain(incomeSeries.flatMap((d) => [Math.abs(d.current), Math.abs(d.previous)]))
  const expensesYDomain = computeYAxisDomain(expensesSeries.flatMap((d) => [Math.abs(d.current), Math.abs(d.previous)]))
  const incomeChartData = incomeSeries.map((d) => {
    const currentDateParsed = new Date(d.period.trim())
    const currentDateLabel = !isNaN(currentDateParsed.getTime()) ? formatPointDate(currentDateParsed) : d.period
    return {
      month: formatChartPeriod(d.period, chartGroupBy),
      current: d.current,
      previous: d.previous,
      currentDate: currentDateLabel,
      previousDate: periodToPreviousYearLabel(d.period),
    }
  })
  const expensesChartData = expensesSeries.map((d) => {
    const currentDateParsed = new Date(d.period.trim())
    const currentDateLabel = !isNaN(currentDateParsed.getTime()) ? formatPointDate(currentDateParsed) : d.period
    return {
      month: formatChartPeriod(d.period, chartGroupBy),
      current: d.current,
      previous: d.previous,
      currentDate: currentDateLabel,
      previousDate: periodToPreviousYearLabel(d.period),
    }
  })

  const chartCurrentLabel = fromDate && toDate ? formatDateRangeForChart(fromDate, toDate) : 'Current'
  const prevYear = fromDate && toDate ? getPreviousYearDates(fromDate, toDate) : { from: '', to: '' }
  const chartPreviousLabel = prevYear.from && prevYear.to ? formatDateRangeForChart(prevYear.from, prevYear.to) : 'Previous'

  // Totals for Income: use profitability API when present; else use sum of filtered series so total and % match chart (Dec–Feb only)
  const incomeSeriesSumCurrent = incomeSeries.reduce((sum, d) => sum + d.current, 0)
  const incomeSeriesSumPrevious = incomeSeries.reduce((sum, d) => sum + d.previous, 0)
  const displayIncomeTotal =
    profitability?.currentSalesRevenue ?? (incomeSeries.length > 0 ? incomeSeriesSumCurrent : incomeValue)
  const displayIncomePrevious =
    profitability?.previousSalesRevenue ?? (incomeSeries.length > 0 ? incomeSeriesSumPrevious : 0)

  // Xero margin logic: Gross = (Gross Profit ÷ Sales Revenue)×100
  const salesRevenue = displayIncomeTotal
  const cogs = profitability?.currentCogs ?? costOfGoodsSold
  const grossProfit = salesRevenue - cogs
  const grossProfitMargin = salesRevenue !== 0 ? (grossProfit / salesRevenue) * 100 : 0

  const previousSalesRevenue = profitability?.previousSalesRevenue ?? 0
  const previousCogs = profitability?.previousCogs ?? 0
  const previousGrossMargin = previousSalesRevenue !== 0 ? ((previousSalesRevenue - previousCogs) / previousSalesRevenue) * 100 : 0
  const grossMarginChange = previousGrossMargin === 0 ? 0 : ((grossProfitMargin - previousGrossMargin) / Math.abs(previousGrossMargin)) * 100
  const marginFromLabel = profitability?.previousShortLabel ?? 'same period last year'

  const cogsPct = salesRevenue !== 0 ? (cogs / salesRevenue) * 100 : 0
  const grossPct = salesRevenue !== 0 ? (grossProfit / salesRevenue) * 100 : 0

  // Net profit margin: chart data and Y domain (allow negative margins)
  const netMarginChartData: { month: string; current: number; previous: number; currentDate?: string; previousDate?: string }[] =
    netProfitMarginResult?.labels?.length && fromDate
      ? (netProfitMarginResult.labels.map((month, i) => {
          const d = new Date(fromDate)
          d.setMonth(d.getMonth() + i)
          const prevD = new Date(d)
          prevD.setFullYear(prevD.getFullYear() - 1)
          return {
            month,
            current: netProfitMarginResult.currentSeries[i] ?? 0,
            previous: netProfitMarginResult.previousSeries[i] ?? 0,
            currentDate: formatPointDate(d),
            previousDate: formatPointDate(prevD),
          }
        }) ?? [])
      : []
  const netMarginAllValues = netMarginChartData.flatMap((d) => [d.current, d.previous]).filter((v) => Number.isFinite(v))
  const netMarginAbsMax = netMarginAllValues.length ? Math.max(...netMarginAllValues.map((v) => Math.abs(v)), 1) : 100
  const netMarginPadded = netMarginAbsMax * 1.15
  const roundToNice = (n: number) => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(n)))
    return Math.ceil(n / magnitude) * magnitude
  }
  const netMarginNice = roundToNice(netMarginPadded)
  const netMarginYDomain: [number, number] = [-netMarginNice, netMarginNice]

  return (
    <div className="space-y-6 p-6" style={{ fontFamily: 'Lexend Deca', maxWidth: '100%' }}>
      {/* Header */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              color: '#212126',
            }}
          >
            Business Snapshot
          </h1>
          <span
            className="text-xs sm:text-sm"
            style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Business Snapshot</span>
          </span>
        </div>

        {/* Tabs */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '4px',
              gap: '0px',
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: isActive ? '#FFFFFF' : '#69697A',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* Date Range Selector + Open in Xero */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 flex-wrap">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom') }} className="border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm" style={{ fontFamily: 'Lexend Deca' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                <div className="relative" ref={presetDropdownRef}>
                  <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setDatePreset('custom') }} className="border border-gray-300 rounded-md pl-9 pr-8 py-2 text-sm" style={{ fontFamily: 'Lexend Deca' }} />
                  <button type="button" onClick={() => setPresetDropdownOpen(!presetDropdownOpen)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded">
                    <FiChevronDown className="w-4 h-4 text-gray-500" />
                  </button>
                  {presetDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1" style={{ fontFamily: 'Lexend Deca' }}>
                      {[
                        { id: 'last-month' as const, label: 'Last month' },
                        { id: 'last-quarter' as const, label: 'Last quarter' },
                        { id: 'last-financial-year' as const, label: 'Last financial year' },
                        { id: 'year-to-date' as const, label: 'Year to date' },
                        { id: 'last-12-months' as const, label: 'Last 12 months' },
                        { id: 'custom' as const, label: 'Custom date range', sub: 'Select or type' },
                      ].map((opt) => {
                        const displayText = opt.id === 'custom' ? opt.sub : formatPresetDisplay(opt.id)
                        return (
                          <button key={opt.id} type="button" onClick={() => { setDatePreset(opt.id); setPresetDropdownOpen(false) }} className="w-full text-left px-4 py-2 hover:bg-gray-50 flex flex-col" style={{ color: datePreset === opt.id ? '#2C71F6' : '#212126' }}>
                            <span className="text-sm font-medium">{opt.label}</span>
                            {displayText && <span className="text-xs text-gray-500 mt-0.5">{displayText}</span>}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button onClick={loadAllReports} disabled={loading} className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#212126', fontFamily: 'Lexend Deca' }}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <a href={XERO_BUSINESS_SNAPSHOT_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-md hover:opacity-90 text-sm font-medium" style={{ backgroundColor: '#13B5EA', fontFamily: 'Lexend Deca' }}>
            <FiExternalLink className="w-4 h-4" /> Open in Xero
          </a>
        </div>
      </div>

      <div className="space-y-6">
        {xeroConnected === null ? (
          <FinancePageLoading message="Checking Xero connection…" />
        ) : !xeroConnected ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-600 mb-4" style={{ fontFamily: 'Lexend Deca' }}>
              Xero is not connected.
            </p>
            <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'Lexend Deca' }}>
              Connect Xero in{' '}
              <Link to={ROUTES.SETTINGS_INTEGRATIONS} className="underline font-medium text-blue-600">
                Settings → Integrations
              </Link>{' '}
              to view your Business Snapshot data.
            </p>
            <button
              onClick={checkXeroStatus}
              className="mt-4 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90"
              style={{ backgroundColor: '#212126', fontFamily: 'Lexend Deca' }}
            >
              Refresh Connection Status
            </button>
          </div>
        ) : loading ? (
          <FinancePageLoading message="Loading Business Snapshot…" />
        ) : !profitLoss ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-600 mb-4" style={{ fontFamily: 'Lexend Deca' }}>
              No Business Snapshot data available.
            </p>
            <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'Lexend Deca' }}>
              {xeroStatus?.isExpired ? (
                <>
                  Your Xero connection has expired. Please{' '}
                  <Link to={ROUTES.SETTINGS_INTEGRATIONS} className="underline font-medium text-blue-600">
                    reconnect Xero
                  </Link>{' '}
                  in Settings → Integrations.
                </>
              ) : (
                <>
                  Unable to load data from Xero. Your connection may need to be refreshed. Please try{' '}
                  <Link to={ROUTES.SETTINGS_INTEGRATIONS} className="underline font-medium text-blue-600">
                    disconnecting and reconnecting Xero
                  </Link>{' '}
                  in Settings → Integrations, or click the button above to open Business Snapshot directly in Xero.
                </>
              )}
            </p>
            <button
              onClick={() => {
                setLoading(true)
                loadAllReports()
              }}
              disabled={loading}
              className="mt-4 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#212126', fontFamily: 'Lexend Deca' }}
            >
              {loading ? 'Retrying...' : 'Retry Loading Data'}
            </button>
          </div>
        ) : (
          <>
            {/* Main Dashboard: Profit or loss (left), Income + Expenses (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Profit or loss — Left column */}
              <div>
                {profitability ? (
                  <ProfitabilityCard
                    currentProfit={profitability.currentProfit}
                    previousProfit={profitability.previousProfit}
                    percentage={profitability.percentage}
                    currentLabel={profitability.currentLabel}
                    previousLabel={profitability.previousLabel}
                    previousShortLabel={profitability.previousShortLabel}
                    currentMonthAbbr={profitability.currentMonthAbbr}
                    previousMonthAbbr={profitability.previousMonthAbbr}
                    useSnapshotStyle
                  />
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-4">Profit or loss</h3>
                    <div className="mb-4">
                      <div className="text-2xl font-semibold text-gray-900 mb-1">
                        {formatCurrency(profitLossValue)}
                      </div>
                      <div className="text-sm text-gray-500 mb-2">Profit or loss</div>
                      {profitData.length > 0 && (() => {
                        const currentTotal = profitLossValue ?? profitData.reduce((sum, d) => sum + d.current, 0)
                        const previousTotal = profitData[profitData.length - 1]?.previous ?? 0
                        const change = calculatePercentageChange(currentTotal, previousTotal)
                        if ((change !== 0 || previousTotal !== 0)) {
                          return (
                            <div className="text-sm flex items-center gap-1" style={{ color: change >= 0 ? '#13AB5B' : '#E93323' }}>
                              {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% from previous period
                            </div>
                          )
                        }
                        return null
                      })()}
                    </div>
                    <p className="text-xs text-gray-500 mt-2" style={{ fontFamily: 'Lexend Deca' }}>
                      Profit is the amount of money made after paying expenses.
                    </p>
                  </div>
                )}
              </div>

              {/* Income + Expenses — Right column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Income — total and % use same range as chart (profitability API or filtered P&L series) */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Income</h3>
                  <div className="mb-4">
                    <div className="text-2xl font-semibold text-gray-900 mb-1" style={{ color: '#9333EA' }}>
                      {formatCurrency(displayIncomeTotal)}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">Income</div>
                    {(() => {
                      const change = calculatePercentageChange(displayIncomeTotal, displayIncomePrevious)
                      if ((change !== 0 || displayIncomePrevious !== 0) && (incomeSeries.length > 0 || profitability != null)) {
                        return (
                          <div className="text-sm flex items-center gap-1" style={{ color: change >= 0 ? '#13AB5B' : '#E93323' }}>
                            {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% from {chartPreviousLabel}
                          </div>
                        )
                      }
                      return null
                    })()}
                  </div>
                  {incomeChartData.length > 0 && (
                    <XeroStyleTimeSeriesChart
                      data={incomeChartData}
                      currentColor="#9c27b0"
                      fillColor="rgba(156, 39, 176, 0.15)"
                      currentLabel={chartCurrentLabel}
                      previousLabel={chartPreviousLabel}
                      height={200}
                      domain={incomeYDomain}
                    />
                  )}
                </div>

                {/* Expenses — total from P&L; % from filtered series so it matches the chart range */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Expenses</h3>
                  <div className="mb-4">
                    <div className="text-2xl font-semibold text-gray-900 mb-1">
                      {formatCurrency(expensesValue)}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">Expenses</div>
                    {/* Breakdown: Cost of Sales + Operating Expenses (from SummaryRow) */}
                    <div className="text-xs text-gray-600 space-y-1 mt-2">
                      {costOfGoodsSold > 0 && (
                        <div>Cost of goods sold: {formatCurrency(costOfGoodsSold)}</div>
                      )}
                      {operatingExpenses > 0 && (
                        <div>Operating expenses: {formatCurrency(operatingExpenses)}</div>
                      )}
                    </div>
                    {(() => {
                      const expensesCurrent = expensesSeries.reduce((sum, d) => sum + d.current, 0)
                      const expensesPrevious = expensesSeries.reduce((sum, d) => sum + d.previous, 0)
                      const change = calculatePercentageChange(expensesCurrent, expensesPrevious)
                      if ((change !== 0 || expensesPrevious !== 0) && expensesSeries.length > 0) {
                        return (
                          <div className="text-sm flex items-center gap-1 mt-2" style={{ color: change <= 0 ? '#13AB5B' : '#E93323' }}>
                            {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% from {chartPreviousLabel}
                          </div>
                        )
                      }
                      return null
                    })()}
                  </div>
                  {expensesChartData.length > 0 && (
                    <XeroStyleTimeSeriesChart
                      data={expensesChartData}
                      currentColor="#F97316"
                      fillColor="rgba(249, 115, 22, 0.15)"
                      currentLabel={chartCurrentLabel}
                      previousLabel={chartPreviousLabel}
                      height={200}
                      domain={expensesYDomain}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Efficiency Section — match Xero Business Snapshot */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6" style={{ fontFamily: 'Lexend Deca' }}>Efficiency</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Gross Profit Margin — Xero: (Gross Profit ÷ Sales Revenue) × 100, bar = COGS % | Gross % */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Gross profit margin</h3>
                  <div className="mb-2">
                    <div className="text-2xl font-semibold text-gray-900 mb-1">
                      {Math.round(grossProfitMargin)}%
                    </div>
                    {(grossMarginChange !== 0 || previousGrossMargin !== 0) && (
                      <div className="text-sm flex items-center gap-1" style={{ color: grossMarginChange >= 0 ? '#13AB5B' : '#E93323' }}>
                        {grossMarginChange >= 0 ? '↑' : '↓'} {Math.abs(grossMarginChange).toFixed(1)}% from {marginFromLabel}
                      </div>
                    )}
                    <div className="text-sm text-gray-500 mt-1">
                      For every ZAR 1 of sales revenue, an average gross profit of ZAR {(grossProfitMargin / 100).toFixed(2)} is made.
                    </div>
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Cost of goods sold</span>
                        <span>{formatCurrency(cogs)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>Gross profit</span>
                        <span>{formatCurrency(grossProfit)}</span>
                      </div>
                      <div className="w-full flex rounded overflow-hidden h-5 mt-2" style={{ background: '#e5e7eb' }}>
                        <div
                          className="h-full flex items-center justify-center transition-all"
                          style={{ width: `${Math.min(100, Math.max(0, cogsPct))}%`, background: '#9ca3af' }}
                          title={`COGS ${cogsPct.toFixed(0)}%`}
                        >
                          {cogsPct >= 15 && <span className="text-xs text-white font-medium">COGS {cogsPct.toFixed(0)}%</span>}
                        </div>
                        <div
                          className="h-full flex items-center justify-center transition-all"
                          style={{ width: `${Math.min(100, Math.max(0, grossPct))}%`, background: '#9333EA' }}
                          title={`Gross ${grossPct.toFixed(0)}%`}
                        >
                          {grossPct >= 15 && <span className="text-xs text-white font-medium">Gross {grossPct.toFixed(0)}%</span>}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Total Sales Revenue: {formatCurrency(salesRevenue)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Net profit margin — Xero: (Net Profit ÷ Income) × 100; blue underline, value in blue, change green/grey, chart purple/dashed grey */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'Lexend Deca' }}>Net profit margin</h3>
                  <div className="h-0.5 w-12 rounded-full mb-4" style={{ background: '#2563eb' }} aria-hidden />
                  <div className="mb-2">
                    <div className="text-3xl font-bold mb-1" style={{ color: '#2563eb', fontFamily: 'Lexend Deca' }}>
                      {netProfitMarginResult != null ? `${Math.round(netProfitMarginResult.currentMargin)}%` : '—'}
                    </div>
                    {netProfitMarginResult != null && (netProfitMarginResult.marginChangePercent !== 0 || netProfitMarginResult.previousMargin !== 0) && (
                      <div className="text-sm flex items-center gap-1 flex-wrap" style={{ fontFamily: 'Lexend Deca' }}>
                        <span style={{ color: netProfitMarginResult.marginChangePercent >= 0 ? '#13AB5B' : '#E93323' }}>
                          {netProfitMarginResult.marginChangePercent >= 0 ? '↑' : '↓'} {Math.abs(netProfitMarginResult.marginChangePercent).toFixed(1)}%
                        </span>
                        <span className="text-gray-600">from {netProfitMarginResult.previousPeriodLabel}</span>
                      </div>
                    )}
                    <div className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Lexend Deca' }}>
                      {netProfitMarginResult != null
                        ? (netProfitMarginResult.explanation ?? `For every ZAR 1 of sales revenue, an average net profit of ZAR ${(netProfitMarginResult.currentMargin / 100).toFixed(2)} is made.`)
                        : 'Net profit margin is calculated from Profit & Loss (accrual).'}
                    </div>
                  </div>
                  {netMarginChartData.length > 0 && (
                    <div className="mt-4">
                      <XeroStyleTimeSeriesChart
                        data={netMarginChartData}
                        currentColor="#7B3FE4"
                        fillColor="rgba(123, 63, 228, 0.08)"
                        currentLabel={chartCurrentLabel}
                        previousLabel={chartPreviousLabel}
                        height={220}
                        domain={netMarginYDomain}
                        yAxisTickFormatter={(v) => `${Math.round(v)}%`}
                        valueSuffix="%"
                        previousStroke="#9CA3AF"
                        previousStrokeDasharray="5 5"
                        gridStroke="#e5e7eb"
                        zeroLineStroke="#9ca3af"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Largest Expenses — Xero Business Snapshot style */}
              <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                        <th
                          className="text-left py-3 px-5"
                          style={{ fontSize: '14px', fontWeight: 600, color: '#212126', minWidth: '300px' }}
                        >
                          <span className="flex items-center gap-1">
                            Largest expenses
                            <FiChevronDown className="w-3.5 h-3.5 text-gray-500" />
                          </span>
                        </th>
                        <th
                          className="text-right py-3 px-5"
                          style={{ fontSize: '13px', fontWeight: 500, color: '#69697A', minWidth: '140px' }}
                        >
                          {expenseLabels.currentLabel || 'Current'}
                        </th>
                        <th
                          className="text-right py-3 px-5"
                          style={{ fontSize: '13px', fontWeight: 500, color: '#69697A', minWidth: '140px' }}
                        >
                          {expenseLabels.previousLabel || 'Previous'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {largestExpenses && largestExpenses.length > 0 ? (
                        <>
                          {largestExpenses.map((expense, idx) => (
                            <tr
                              key={idx}
                              style={{
                                borderBottom: '1px solid #F3F4F6',
                                transition: 'background-color 0.15s',
                              }}
                              className="hover:bg-gray-50"
                            >
                              <td className="py-4 px-5" style={{ color: '#212126', fontWeight: 400 }}>
                                {expense.name}
                              </td>
                              <td className="py-4 px-5 text-right" style={{ fontWeight: 500, color: '#212126' }}>
                                <span className="inline-flex items-center gap-1.5">
                                  {expense.current.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                  {expense.change === 'up' && (
                                    <span style={{ color: '#E93323', fontSize: '13px', fontWeight: 600 }}>↑</span>
                                  )}
                                  {expense.change === 'down' && (
                                    <span style={{ color: '#13AB5B', fontSize: '13px', fontWeight: 600 }}>↓</span>
                                  )}
                                </span>
                              </td>
                              <td className="py-4 px-5 text-right" style={{ color: '#69697A', fontWeight: 400 }}>
                                {expense.previous.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                              </td>
                            </tr>
                          ))}
                          {directorRemuneration && !largestExpenses.some((e) => e.name.toLowerCase().includes('director')) && (
                            <tr
                              style={{
                                borderBottom: '1px solid #F3F4F6',
                                transition: 'background-color 0.15s',
                              }}
                              className="hover:bg-gray-50"
                            >
                              <td className="py-4 px-5" style={{ color: '#212126', fontWeight: 400 }}>
                                {directorRemuneration.name}
                              </td>
                              <td className="py-4 px-5 text-right" style={{ fontWeight: 500, color: '#212126' }}>
                                <span className="inline-flex items-center gap-1.5">
                                  {directorRemuneration.current.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                  {directorRemuneration.change === 'up' && (
                                    <span style={{ color: '#E93323', fontSize: '13px', fontWeight: 600 }}>↑</span>
                                  )}
                                  {directorRemuneration.change === 'down' && (
                                    <span style={{ color: '#13AB5B', fontSize: '13px', fontWeight: 600 }}>↓</span>
                                  )}
                                </span>
                              </td>
                              <td className="py-4 px-5 text-right" style={{ color: '#69697A', fontWeight: 400 }}>
                                {directorRemuneration.previous.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                              </td>
                            </tr>
                          )}
                        </>
                      ) : directorRemuneration ? (
                        <tr
                          style={{
                            borderBottom: '1px solid #F3F4F6',
                            transition: 'background-color 0.15s',
                          }}
                          className="hover:bg-gray-50"
                        >
                          <td className="py-4 px-5" style={{ color: '#212126', fontWeight: 400 }}>
                            {directorRemuneration.name}
                          </td>
                          <td className="py-4 px-5 text-right" style={{ fontWeight: 500, color: '#212126' }}>
                            <span className="inline-flex items-center gap-1.5">
                              {directorRemuneration.current.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                              {directorRemuneration.change === 'up' && (
                                <span style={{ color: '#E93323', fontSize: '13px', fontWeight: 600 }}>↑</span>
                              )}
                              {directorRemuneration.change === 'down' && (
                                <span style={{ color: '#13AB5B', fontSize: '13px', fontWeight: 600 }}>↓</span>
                              )}
                            </span>
                          </td>
                          <td className="py-4 px-5 text-right" style={{ color: '#69697A', fontWeight: 400 }}>
                            {directorRemuneration.previous.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td colSpan={3} className="py-6 px-5 text-center" style={{ color: '#9CA3AF', fontSize: '13px' }}>
                            No expense data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            {/* Financial position and cash — shown for all filters (last month, quarter, last FY, YTD, last 12 months) */}
            <div className="mt-8" style={{ fontFamily: 'Lexend Deca' }}>
              <h2 className="text-base font-medium text-gray-900 mb-6">
                Financial position and cash
                {financialPosition ? ` — ${new Date(financialPosition.snapshotDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
              </h2>
              {financialPosition ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Balance sheet — same fields as reference, with visual bar */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                      Balance sheet
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm text-gray-600">Assets:</span>
                        <span className="text-base font-semibold text-gray-900">{financialPosition.balanceSheet.assets.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <hr className="border-gray-200" />
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm text-gray-600">Liabilities:</span>
                        <span className="text-base font-semibold text-gray-900">{financialPosition.balanceSheet.liabilities.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm text-gray-600">Equity:</span>
                        <span className="text-base font-semibold" style={{ color: financialPosition.balanceSheet.equity < 0 ? '#E93323' : '#212126' }}>{financialPosition.balanceSheet.equity.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                    {/* Visual: Assets (gray) | Liabilities + Equity (blue split), proportional to selected-period snapshot */}
                    {financialPosition.balanceSheet.assets > 0 && (() => {
                      const liab = Math.abs(financialPosition.balanceSheet.liabilities)
                      const eq = Math.abs(financialPosition.balanceSheet.equity)
                      const total = liab + eq || 1
                      const liabPct = (liab / total) * 100
                      return (
                        <div className="flex rounded overflow-hidden h-6 mt-4" style={{ background: '#e5e7eb' }}>
                          <div
                            className="h-full flex items-center justify-center text-xs font-medium text-gray-700"
                            style={{ width: '50%', minWidth: 60, background: '#9ca3af' }}
                            title="Assets"
                          >
                            Assets
                          </div>
                          <div className="h-full flex flex-1" style={{ minWidth: 60 }}>
                            <div
                              className="h-full flex items-center justify-center text-xs font-medium text-white"
                              style={{ width: `${liabPct}%`, background: '#3b82f6' }}
                              title="Liabilities"
                            >
                              {liabPct >= 15 ? 'Liabilities' : ''}
                            </div>
                            <div
                              className="h-full flex items-center justify-center text-xs font-medium text-white"
                              style={{ width: `${100 - liabPct}%`, background: '#60a5fa' }}
                              title="Equity"
                            >
                              {100 - liabPct >= 15 ? 'Equity' : ''}
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                    <p className="text-xs text-gray-500 mt-4">Assets are equal to liabilities plus equity</p>
                  </div>

                  {/* Overall cash balance */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                      Overall cash balance
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-400 text-gray-500 text-xs" title="Sum of all bank and cash accounts"><FiInfo className="w-3 h-3" /></span>
                    </h3>
                    <div className="text-2xl font-bold text-gray-900 mb-4">
                      {financialPosition.cashBalance.total.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>
                    <div className="space-y-2 mb-4">
                      {financialPosition.cashBalance.accounts.map((acc) => (
                        <div key={acc.name} className="flex justify-between text-sm">
                          <span className="text-gray-600 truncate pr-2">{acc.name}</span>
                          <span className="font-medium shrink-0" style={{ color: acc.balance < 0 ? '#E93323' : '#212126' }}>{acc.balance.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      Based on {financialPosition.cashBalance.totalAccounts} of {financialPosition.cashBalance.totalAccounts} accounts
                    </p>
                    <a
                      href="https://go.xero.com/app/!q98Pl/cashflow"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-blue-600 hover:underline"
                      style={{ fontFamily: 'Lexend Deca' }}
                    >
                      See your upcoming cash flow
                      <FiExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  {/* Average time to get paid */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                      Average time to get paid
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-400 text-gray-500 text-xs" title="Average days customers take to pay invoices"><FiInfo className="w-3 h-3" /></span>
                    </h3>
                    <div className="text-2xl font-bold text-gray-900 mb-2">{financialPosition.avgTimeToGetPaid.days ?? 0} days</div>
                    {financialPosition.avgTimeToGetPaid.previousPeriodDays != null && financialPosition.avgTimeToGetPaid.previousPeriodLabel && (
                      <div className="flex items-center gap-1 text-sm mb-2" style={{ color: '#13AB5B' }}>
                        <span>↓</span>
                        <span>{Math.abs(financialPosition.avgTimeToGetPaid.days - financialPosition.avgTimeToGetPaid.previousPeriodDays)} days from {financialPosition.avgTimeToGetPaid.previousPeriodLabel}</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-600">Value of unpaid invoices: {financialPosition.avgTimeToGetPaid.unpaidInvoicesValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                  </div>

                  {/* Average time to pay suppliers */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                      Average time to pay suppliers
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-400 text-gray-500 text-xs" title="Average days to pay bills"><FiInfo className="w-3 h-3" /></span>
                    </h3>
                    <div className="text-2xl font-bold text-gray-900 mb-2">{financialPosition.avgTimeToPaySuppliers.days ?? 0} days</div>
                    {financialPosition.avgTimeToPaySuppliers.previousPeriodDays != null && financialPosition.avgTimeToPaySuppliers.previousPeriodLabel && (
                      <div className="flex items-center gap-1 text-sm mb-2" style={{ color: '#13AB5B' }}>
                        <span>↓</span>
                        <span>{Math.abs(financialPosition.avgTimeToPaySuppliers.days - financialPosition.avgTimeToPaySuppliers.previousPeriodDays)} days from {financialPosition.avgTimeToPaySuppliers.previousPeriodLabel}</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-600">Value of unpaid bills: {financialPosition.avgTimeToPaySuppliers.unpaidBillsValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <p className="text-sm text-gray-500">No financial position data for the selected period.</p>
                </div>
              )}
            </div>

            {/* Financial Ratios — 6 Xero formulas from Balance Sheet */}
            {financialRatiosResult && (
              <div className="mt-8" style={{ fontFamily: 'Lexend Deca' }}>
                <h2 className="text-base font-medium text-gray-900 mb-6">
                  Financial ratios — {formatDateRangeForChart(fromDate, toDate)}
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  Snapshot at end of period ({financialRatiosResult.snapshotDate ? new Date(financialRatiosResult.snapshotDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}) vs same date last year. All ratios from Balance Sheet.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { key: 'currentRatio' as const, title: 'Current ratio' },
                    { key: 'debtRatio' as const, title: 'Debt ratio' },
                    { key: 'currentLiabilitiesToNetWorth' as const, title: 'Current liabilities to net worth' },
                    { key: 'debtToEquity' as const, title: 'Debt to equity ratio' },
                    { key: 'workingCapitalToTotalAssets' as const, title: 'Working capital to total assets' },
                    { key: 'fixedAssetsToNetWorth' as const, title: 'Fixed assets to net worth' },
                  ].map(({ key, title }) => {
                    const item = financialRatiosResult.ratios[key]
                    const chartData = financialRatiosResult.chartData[key] || []
                    const formatVal = (v: number) => v.toFixed(2)
                    // Working capital to total assets can be negative; use a symmetric domain so scale matches Xero (e.g. -0.30 to 0.30)
                    const ratioDomain: [number, number] | undefined =
                      key === 'workingCapitalToTotalAssets' && chartData.length > 0
                        ? (() => {
                            const allV = chartData.flatMap((d) => [d.current, d.previous])
                            const minV = Math.min(0, ...allV)
                            const maxV = Math.max(0, ...allV)
                            const pad = (maxV - minV) * 0.15 || 0.1
                            const step = 0.15
                            const low = Math.floor((minV - pad) / step) * step
                            const high = Math.ceil((maxV + pad) / step) * step
                            return [low, high]
                          })()
                        : undefined
                    return (
                      <div key={key} className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
                        <div className="h-0.5 w-12 rounded-full mb-3" style={{ background: '#2563eb' }} aria-hidden />
                        <div className="mb-2">
                          <div className="text-2xl font-bold mb-1" style={{ color: '#2563eb' }}>{formatVal(item.current)}</div>
                          {(item.change !== 0 || item.previous !== 0) && (
                            <div className="text-sm flex items-center gap-1">
                              <span style={{ color: item.trend === 'up' ? '#13AB5B' : '#E93323' }}>
                                {item.trend === 'up' ? '↑' : '↓'} {formatVal(Math.abs(item.change))}
                              </span>
                              <span className="text-gray-600">from {financialRatiosResult.previousLabel}</span>
                            </div>
                          )}
                        </div>
                        {chartData.length > 0 && (
                          <div className="mt-4">
                            <XeroStyleTimeSeriesChart
                              data={chartData}
                              currentColor="#7B3FE4"
                              fillColor="rgba(123, 63, 228, 0.08)"
                              currentLabel={formatDateRangeForChart(fromDate, toDate)}
                              previousLabel={chartPreviousLabel}
                              height={180}
                              domain={ratioDomain}
                              yAxisTickFormatter={(v) => formatVal(Number(v))}
                              tooltipValueFormatter={(v) => formatVal(Number(v))}
                              previousStroke="#9CA3AF"
                              previousStrokeDasharray="5 5"
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
